import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftOutlined,
  ReadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Input, List, Card, Tooltip, Button, Divider } from 'antd';
import { postsSelector, store, tagsSelector } from 'src/store';
import { fetchTagById, Tag, removeAllTags } from 'src/slices/tag';
import { createUseStyles } from 'react-jss';
import { fetchPostsByTagsId, Post, removeAllPosts } from 'src/slices/post';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import AddArticle from './AddArticle';
import { findUsersByIds, User } from 'src/slices/auth';
import { useTeamsFx } from 'src/utils/useTeamsFx';

const useStyles = createUseStyles({
  page: {
    margin: '20px 5% 0 5%',
    '& .ant-divider-vertical': {
      height: '3em',
      verticalAlign: 'text-top',
    },
    '& .ant-divider-horizontal': {
      borderTop: '2px solid rgba(0, 0, 0, 0.06)',
    },
  },
  pageHeader: {
    display: 'flex',
    fontSize: '1.5rem',
  },
  previousButton: {
    display: 'inline-block',
    width: '5%',
    textAlign: 'center',
    marginLeft: '20px',
    marginRight: '20px',
  },
  searchArticles: {
    display: 'inline-block',
    width: '45%',
  },
  searchInput: {
    width: '50%',
  },
  headerInfo: {
    display: 'inline-block',
    width: '50%',
    textAlign: 'right',
    marginRight: '20px',
  },
  headerTitle: {
    fontSize: '3rem',
    textAlign: 'center',
  },
  content: {
    marginRight: '5%',
    marginLeft: '5%',
  },
  card: {
    borderRadius: '5px',
    boxShadow: '0 0 5px #ccc',
    height: 'auto',
    '& .ant-card-head-title ': {
      whiteSpace: 'pre-wrap',
      fontWeight: 'bolder',
      color: '#5c5c5c',
    },
    '& .ant-card-body': {
      paddingTop: '10px',
      paddingBottom: '10px',
    },
  },
  cardContent: {
    height: 'auto',
  },
  cardDescription: {
    height: 'auto',
    marginBottom: '5%',
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '10%',
    paddingBottom: '5%',
  },
  readBox: {
    display: 'inline-block',
  },
});

const ListArticles = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const { context } = useTeamsFx();
  const [loadedTeamId, setLoadedTeamId] = useState<boolean>(false);

  const { tagId }: { tagId: string } = useParams();
  const tag: Tag | undefined = tagsSelector.selectById(store.getState(), tagId);

  const posts: Post[] = useAppSelector(postsSelector.selectAll);
  const [selectedPosts, setSelectedPosts] = useState<Post[]>(posts);

  const [loading, setLoading] = useState<boolean>(true);

  const { users }: { users: { [userId: string]: User } } = useAppSelector(
    ({ auth: { users } }) => ({ users }),
  );

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchTagById(tagId));
      dispatch(fetchPostsByTagsId([tagId]));
    };
    fetchData();
    return () => {
      dispatch(removeAllPosts());
      dispatch(removeAllTags());
    };
  }, []);

  useEffect(() => {
    setLoadedTeamId(true);
  }, [context?.teamId]);

  useEffect(() => {
    const findUsersAndDisplay = async () => {
      const userIds = posts.map((post) => post.authorId as string);
      await dispatch(
        findUsersByIds(userIds.filter((n, i) => userIds.indexOf(n) === i)),
      );
      setSelectedPosts(posts);
      setLoading(false);
    };

    findUsersAndDisplay();
  }, [posts]);

  const filterArticles = (e: any) => {
    if (e.target.value == '') {
      setSelectedPosts(posts);
    } else {
      const filteredPosts = posts.filter((post) => {
        return post.title.includes(e.target.value);
      });
      setSelectedPosts(filteredPosts);
    }
  };

  const openArticle = (e: any, post: Post) => {
    history.push(`/posts/${post._id}`);
  };

  const goToPreviousPage = () => {
    history.goBack();
  };

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <div className={classes.previousButton}>
          <Tooltip title="Previous">
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowLeftOutlined />}
              size="large"
              onClick={() => {
                goToPreviousPage();
              }}
            />
          </Tooltip>
        </div>

        <div className={classes.searchArticles}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t('tags.searchArticles')}
            className={classes.searchInput}
            onChange={(e) => filterArticles(e)}
            allowClear
          />
          <Divider type="vertical" />
          {loadedTeamId && (
            <AddArticle defaultTag={tag?.name} teamId={context?.teamId} />
          )}
        </div>

        <div className={classes.headerInfo}>
          {t('listArticle.totalNumberOf')}
          {posts.length > 1 ? 'articles' : 'article'} {posts.length}
        </div>
      </div>
      <Divider />

      <div className={classes.content}>
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 1,
            md: 1,
            lg: 2,
            xl: 2,
            xxl: 3,
          }}
          locale={t('common.emptyText')}
          dataSource={selectedPosts.length ? selectedPosts : posts}
          renderItem={(post) => (
            <List.Item>
              <Card
                className={classes.card}
                loading={loading}
                hoverable={true}
                title={post.title}
              >
                <div className={classes.cardContent}>
                  <div className={classes.cardDescription}>
                    {post.description}
                  </div>
                  <div className={classes.cardBottom}>
                    <div>
                      {`${t('tags.articleAdded')}
                            ${
                              post?.authorId
                                ? users[post.authorId as string]?.firstName
                                : null
                            }
                            `}
                    </div>
                    <div key="read" className={classes.readBox}>
                      <Button
                        type="primary"
                        shape="round"
                        onClick={(e) => openArticle(e, post)}
                      >
                        {t('listArticle.read')}
                        <ReadOutlined />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </List.Item>
          )}
        />
      </div>
    </div>
  );
};

export default ListArticles;
